import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Spinner,
  Box,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile } from "@fortawesome/free-solid-svg-icons";
import { getSupabase, supabaseSchema } from "./supabaseClient";
import {
  extractAllSubmissionLinks,
  extractEpicFormEntries,
} from "../utils/submissionUtils";
import DraggableResizableModal from "./DraggableResizableModal";
import { isQualifyingFormName } from "../utils/formProcessingUtils";
import { toSignedUrl } from "../utils/signedUrls";
import AssistedFormBadge from "./AssistedFormBadge";
import { getAssistedForms } from "../pages/medstudent/agents/agentsService";

// The submission ids of a visit's submitted forms (".../<form_id>/submission/<id>"), as the
// assistant's submissions are looked up by (getAssistedForms).
const submissionIdsOf = (links) =>
  links
    .map((link) =>
      typeof link === "string" ? link.match(/\/submission\/([^/?#]+)/) : null,
    )
    .filter(Boolean)
    .map((found) => found[1]);

/**
 * Shared FormsModal component for viewing and managing patient forms.
 * Used by PatientPage, EditableDocumentPage, and SchedulesTable.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Function to close the modal
 * @param {string} props.patientId - The patient ID to fetch forms for
 * @param {Object} props.patientData - Optional patient data object (for display purposes)
 * @param {Object} props.processingForms - Optional object tracking forms being processed in background { "form_id_submission_id": true }
 * @param {function} props.onProcessForms - Optional callback to handle "Process Forms" button click, receives array of qualifying form links
 */
const FormsModal = ({
  isOpen,
  onClose,
  patientId,
  patientData = null,
  processingForms = {},
  onProcessForms = null,
  readOnly = false,
}) => {
  const toast = useToast();
  // Cross-schema (linked-provider) patients carry their home schema on the
  // patientData prop; falsy => byte-identical to pre-Task-6 behavior (plain client).
  const pschema = patientData?._schema;

  // Forms state
  const [submissionLinks, setSubmissionLinks] = useState([]);
  // The submissions the forms agent's assistant made for the patient (unsigned), by submission id:
  // each gets AssistedFormBadge. A lookup that fails marks none.
  const [assistedForms, setAssistedForms] = useState({});
  const [formMetadata, setFormMetadata] = useState({});
  const [formNames, setFormNames] = useState({}); // Map of form_id to form_name
  const [isLoadingForms, setIsLoadingForms] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track if this is the first load

  // Epic forms (from StoringFormSubmission, no URL)
  const [epicForms, setEpicForms] = useState([]);
  const [extractingEpicForms, setExtractingEpicForms] = useState({});

  // Extraction state
  const [extractingForms, setExtractingForms] = useState({});

  // Extracted data modal state
  const [isExtractedDataModalOpen, setIsExtractedDataModalOpen] =
    useState(false);
  const [extractedData, setExtractedData] = useState({
    full_text: null,
    summary: null,
    updated_at: null,
  });
  const [isLoadingExtractedData, setIsLoadingExtractedData] = useState(false);
  const [selectedFormInfo, setSelectedFormInfo] = useState({
    form_id: null,
    submission_id: null,
  });
  const [selectedFormLink, setSelectedFormLink] = useState(null); // Store the current link for re-extraction
  const [isReExtractingFormData, setIsReExtractingFormData] = useState(false); // Track re-extraction in modal

  // DB entry missing confirmation modal state
  const {
    isOpen: isDbMissingModalOpen,
    onOpen: openDbMissingModal,
    onClose: closeDbMissingModal,
  } = useDisclosure();
  const [dbMissingFormLink, setDbMissingFormLink] = useState(null);
  const [dbMissingFormInfo, setDbMissingFormInfo] = useState({
    form_id: null,
    submission_id: null,
  });
  const [existingDbEntry, setExistingDbEntry] = useState(null); // If found entry with different submission_id
  const [isCheckingDb, setIsCheckingDb] = useState(false);
  const [isCreatingDbEntry, setIsCreatingDbEntry] = useState(false);

  // Extract form_id and submission_id from a submission URL
  const extractFormIds = (url) => {
    if (typeof url !== "string") return { form_id: null, submission_id: null };

    // Try format: /form_id/submission/submission_id
    const match = url.match(/\/([^/]+)\/submission\/([^/]+)/);
    if (match) {
      return { form_id: match[1], submission_id: match[2] };
    }

    // Try format: URL ending with a single UUID (e.g., https://wtf-forms.../e156a3cd-e5f8-4422-b1c0-4ff71f352ee9)
    // In this case, the UUID is used as both form_id and submission_id
    const uuidMatch = url.match(
      /\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i,
    );
    if (uuidMatch) {
      return { form_id: uuidMatch[1], submission_id: uuidMatch[1] };
    }

    return { form_id: null, submission_id: null };
  };

  // Check if a DB entry exists for the form_id (regardless of submission_id)
  const checkExistingDbEntry = async (form_id, submission_id) => {
    try {
      const supabase = await getSupabase();
      const client = pschema ? supabase.schema(pschema) : supabase;

      // First check for exact match (form_id + submission_id)
      const { data: exactMatch, error: exactError } = await client
        .from("StoringFormSubmission")
        .select(
          "id, form_id, submission_id, PatientName, MetaData, updated_at",
        )
        .eq("form_id", form_id)
        .eq("submission_id", submission_id)
        .maybeSingle();

      if (!exactError && exactMatch) {
        // Exact match found - this shouldn't happen if metadata is missing, but handle it
        return { type: "exact", entry: exactMatch };
      }

      // Check if the row's id column matches the submission_id from the URL
      // This handles cases where submission_id is null but the row's primary key matches
      const { data: idMatch, error: idMatchError } = await client
        .from("StoringFormSubmission")
        .select(
          "id, form_id, submission_id, PatientName, MetaData, updated_at",
        )
        .eq("form_id", form_id)
        .eq("id", submission_id)
        .maybeSingle();

      if (!idMatchError && idMatch) {
        // Found entry where id column matches submission_id - treat as exact match needing update
        return { type: "id_match", entry: idMatch };
      }

      // Check for entry with same form_id but different submission_id
      const { data: formIdMatch, error: formIdError } = await client
        .from("StoringFormSubmission")
        .select(
          "id, form_id, submission_id, PatientName, MetaData, updated_at",
        )
        .eq("form_id", form_id)
        .limit(1)
        .maybeSingle();

      if (!formIdError && formIdMatch) {
        // Found entry with same form_id but different submission_id
        return { type: "form_id_match", entry: formIdMatch };
      }

      // No entry found at all
      return { type: "not_found", entry: null };
    } catch (error) {
      console.error("Error checking existing DB entry:", error);
      return { type: "error", entry: null, error };
    }
  };

  // Handle click on extract button when metadata is missing
  const handleMissingMetadataClick = async (link) => {
    const { form_id, submission_id } = extractFormIds(link);

    if (!form_id || !submission_id) {
      toast({
        title: "Error",
        description:
          "Could not extract form ID and submission ID from the link",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsCheckingDb(true);
    setDbMissingFormLink(link);
    setDbMissingFormInfo({ form_id, submission_id });

    try {
      const result = await checkExistingDbEntry(form_id, submission_id);

      if (result.type === "exact") {
        // Entry exists with exact match - shouldn't happen but proceed with extraction
        toast({
          title: "Entry Found",
          description: "DB entry already exists. Proceeding with extraction.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        await handleExtractFormTextMetadata(link);
        return;
      }

      if (result.type === "id_match") {
        // Found entry where row's id column matches the URL's submission_id
        // Auto-update submission_id and proceed with extraction
        try {
          const supabase = await getSupabase();
          const client = pschema ? supabase.schema(pschema) : supabase;
          console.log(
            "🔄 FormsModal: Auto-updating submission_id for row",
            result.entry.id,
          );
          await client
            .from("StoringFormSubmission")
            .update({ submission_id: submission_id })
            .eq("id", result.entry.id);

          toast({
            title: "Entry Updated",
            description:
              "Database entry found and updated. Proceeding with extraction.",
            status: "success",
            duration: 3000,
            isClosable: true,
          });

          // Refresh forms metadata to get the updated entry
          await fetchFormsData();
          await handleExtractFormTextMetadata(link);
        } catch (updateError) {
          console.error("Error auto-updating submission_id:", updateError);
          toast({
            title: "Error",
            description: "Found matching entry but failed to update it",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
        return;
      }

      if (result.type === "form_id_match") {
        // Found entry with same form_id but different submission_id
        setExistingDbEntry(result.entry);
        openDbMissingModal();
      } else if (result.type === "not_found") {
        // No entry found at all
        setExistingDbEntry(null);
        openDbMissingModal();
      } else {
        // Error occurred
        toast({
          title: "Error",
          description: "Failed to check database for existing entry",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error handling missing metadata click:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to check database",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCheckingDb(false);
    }
  };

  // Create or update DB entry when user confirms
  const handleConfirmDbEntry = async () => {
    const { form_id, submission_id } = dbMissingFormInfo;

    if (!form_id || !submission_id) {
      closeDbMissingModal();
      return;
    }

    setIsCreatingDbEntry(true);

    try {
      const supabase = await getSupabase();
      const client = pschema ? supabase.schema(pschema) : supabase;

      if (existingDbEntry) {
        // Update existing entry's submission_id
        const { error: updateError } = await client
          .from("StoringFormSubmission")
          .update({ submission_id: submission_id })
          .eq("id", existingDbEntry.id);

        if (updateError) {
          throw updateError;
        }

        toast({
          title: "Entry Updated",
          description: "Database entry updated with new submission ID",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new entry
        const { error: insertError } = await client
          .from("StoringFormSubmission")
          .insert({
            form_id: form_id,
            submission_id: submission_id,
          });

        if (insertError) {
          throw insertError;
        }

        toast({
          title: "Entry Created",
          description: "Database entry created successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }

      closeDbMissingModal();

      // Refresh forms metadata to get the new entry
      await fetchFormsData();

      // Now proceed with extraction
      if (dbMissingFormLink) {
        await handleExtractFormTextMetadata(dbMissingFormLink);
      }
    } catch (error) {
      console.error("Error creating/updating DB entry:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create/update database entry",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCreatingDbEntry(false);
    }
  };

  // Fetch forms data - showLoading controls whether to show the loading spinner (only on initial load)
  const fetchFormsData = useCallback(
    async (showLoading = true) => {
      if (!patientId) {
        console.log("🔍 FormsModal: No patientId provided, skipping fetch");
        return;
      }

      console.log(
        "🔍 FormsModal: Fetching forms for patient:",
        patientId,
        "showLoading:",
        showLoading,
      );

      // Only show loading spinner on initial load, not during background refreshes
      if (showLoading) {
        setIsLoadingForms(true);
      }

      try {
        const links = await extractAllSubmissionLinks(patientId, pschema);
        console.log(
          "🔍 FormsModal: Extracted submission links:",
          links,
          "count:",
          links.length,
        );
        setSubmissionLinks(links);

        // Fetch metadata for each form
        const supabase = await getSupabase();
        const client = pschema ? supabase.schema(pschema) : supabase;
        const metadataMap = {};

        for (const link of links) {
          const { form_id, submission_id } = extractFormIds(link);
          if (form_id && submission_id) {
            try {
              // First try exact match by form_id and submission_id
              let { data, error } = await client
                .from("StoringFormSubmission")
                .select(
                  "id, submission_id, PatientName, MetaData, full_text, updated_at, created_at",
                )
                .eq("form_id", form_id)
                .eq("submission_id", submission_id)
                .maybeSingle();

              // If column error, retry without full_text
              if (error?.message?.includes("full_text")) {
                ({ data, error } = await client
                  .from("StoringFormSubmission")
                  .select("id, submission_id, PatientName, MetaData, updated_at, created_at")
                  .eq("form_id", form_id)
                  .eq("submission_id", submission_id)
                  .maybeSingle());
              }

              // If not found, try matching by form_id and id column (row's primary key)
              // This handles cases where submission_id is null but the row's id matches the URL's submission_id
              if (!data && !error) {
                let { data: idMatchData, error: idMatchError } =
                  await client
                    .from("StoringFormSubmission")
                    .select(
                      "id, submission_id, PatientName, MetaData, full_text, updated_at, created_at",
                    )
                    .eq("form_id", form_id)
                    .eq("id", submission_id)
                    .maybeSingle();

                if (idMatchError?.message?.includes("full_text")) {
                  ({ data: idMatchData, error: idMatchError } = await client
                    .from("StoringFormSubmission")
                    .select("id, submission_id, PatientName, MetaData, updated_at, created_at")
                    .eq("form_id", form_id)
                    .eq("id", submission_id)
                    .maybeSingle());
                }

                if (!idMatchError && idMatchData) {
                  data = idMatchData;
                  console.log(
                    "🔍 FormsModal: Found match by id column for",
                    `${form_id}_${submission_id}`,
                  );

                  // Auto-update the submission_id if it's null or different
                  if (
                    !idMatchData.submission_id ||
                    idMatchData.submission_id !== submission_id
                  ) {
                    console.log(
                      "🔄 FormsModal: Updating submission_id for row",
                      idMatchData.id,
                    );
                    await client
                      .from("StoringFormSubmission")
                      .update({ submission_id: submission_id })
                      .eq("id", idMatchData.id);
                  }
                }
              }

              if (!error && data) {
                metadataMap[`${form_id}_${submission_id}`] = {
                  PatientName: data.PatientName,
                  MetaData: data.MetaData,
                  full_text: data.full_text,
                  updated_at: data.updated_at,
                  created_at: data.created_at,
                };
                console.log(
                  "🔍 FormsModal: Updated metadata for",
                  `${form_id}_${submission_id}`,
                  "full_text:",
                  !!data.full_text,
                  "updated_at:",
                  data.updated_at,
                );
              }
            } catch (err) {
              console.warn(
                `⚠️ Error fetching metadata for form ${form_id}/${submission_id}:`,
                err,
              );
            }
          }
        }

        console.log(
          "🔍 FormsModal: Final metadataMap keys:",
          Object.keys(metadataMap),
        );
        setFormMetadata(metadataMap);

        // Fetch form names from StoringFormLink table to identify qualifying forms
        const namesMap = {};
        for (const link of links) {
          const { form_id } = extractFormIds(link);
          if (form_id && !namesMap[form_id]) {
            try {
              const { data: linkData, error: linkError } = await client
                .from("StoringFormLink")
                .select("form_name")
                .eq("id", form_id)
                .maybeSingle();

              if (!linkError && linkData && linkData.form_name) {
                namesMap[form_id] = linkData.form_name;
                console.log(
                  "🔍 FormsModal: Found form_name for",
                  form_id,
                  ":",
                  linkData.form_name,
                );
              }
            } catch (err) {
              console.warn(
                `⚠️ Error fetching form_name for form ${form_id}:`,
                err,
              );
            }
          }
        }
        console.log("🔍 FormsModal: Form names map:", namesMap);
        setFormNames(namesMap);

        // Fetch Epic forms (QuestionnaireResponse from Epic, no URL)
        try {
          const epicEntries = await extractEpicFormEntries(patientId, pschema);
          if (epicEntries.length > 0) {
            const epicRows = [];
            for (const entry of epicEntries) {
              let { data: row, error: epicRowErr } = await client
                .from("StoringFormSubmission")
                .select(
                  "id, form_id, submission_id, PatientName, MetaData, full_text, summary, updated_at, created_at",
                )
                .eq("form_id", entry.form_id)
                .maybeSingle();
              if (epicRowErr?.message?.includes("full_text") || epicRowErr?.message?.includes("summary")) {
                ({ data: row } = await client
                  .from("StoringFormSubmission")
                  .select("id, form_id, submission_id, PatientName, MetaData, updated_at, created_at")
                  .eq("form_id", entry.form_id)
                  .maybeSingle());
              }
              if (row) {
                epicRows.push(row);
              }
            }
            setEpicForms(epicRows);
            console.log("🏥 FormsModal: Epic forms loaded:", epicRows.length);
          } else {
            setEpicForms([]);
          }
        } catch (epicErr) {
          console.warn("⚠️ FormsModal: Error fetching Epic forms:", epicErr);
          setEpicForms([]);
        }

        // Mark initial load as complete
        if (showLoading) {
          setIsInitialLoad(false);
        }
      } catch (error) {
        console.error("❌ Error fetching submission links:", error);
        setSubmissionLinks([]);
        setFormMetadata({});
        setFormNames({});
        setEpicForms([]);
      } finally {
        if (showLoading) {
          setIsLoadingForms(false);
        }
      }
    },
    [patientId, pschema],
  );

  // Fetch forms when modal opens
  useEffect(() => {
    console.log(
      "🔍 FormsModal useEffect: isOpen=",
      isOpen,
      "patientId=",
      patientId,
    );
    if (isOpen && patientId) {
      console.log("🔍 FormsModal: Triggering fetchFormsData (initial load)");
      setIsInitialLoad(true);
      fetchFormsData(true); // Show loading on initial open
    }
  }, [isOpen, patientId, fetchFormsData]);

  // Poll for updates when forms are being processed OR when there are forms without full_text
  useEffect(() => {
    if (!isOpen || !patientId) return;

    // Check if any forms are being processed OR if we have forms without full_text
    const hasProcessingForms = Object.keys(processingForms).length > 0;
    const hasFormsWithoutData = Object.values(formMetadata).some(
      (m) => !m.full_text,
    );

    // Only poll if there are forms being processed or forms missing data
    if (!hasProcessingForms && !hasFormsWithoutData) return;

    console.log(
      "🔍 FormsModal: Starting polling - processingForms:",
      Object.keys(processingForms).length,
      "formsWithoutData:",
      hasFormsWithoutData,
    );

    // Poll every 3 seconds to check for updates (background refresh - no loading spinner)
    const pollInterval = setInterval(() => {
      console.log("🔍 FormsModal: Polling for form updates (background)...");
      fetchFormsData(false); // Don't show loading during polling
    }, 3000);

    return () => {
      console.log("🔍 FormsModal: Stopping polling");
      clearInterval(pollInterval);
    };
  }, [isOpen, patientId, processingForms, formMetadata, fetchFormsData]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Don't reset immediately to avoid flicker on close
      const timeout = setTimeout(() => {
        setSubmissionLinks([]);
        setFormMetadata({});
        setFormNames({});
        setExtractingForms({});
        setEpicForms([]);
        setExtractingEpicForms({});
        setIsInitialLoad(true); // Reset for next open
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  // Compute qualifying forms (forms that match the specific questionnaire titles)
  useEffect(() => {
    if (!isOpen) return undefined;
    const ids = submissionIdsOf(submissionLinks);
    if (ids.length === 0) {
      setAssistedForms({});
      return undefined;
    }
    let live = true;
    getAssistedForms(pschema || supabaseSchema || "public", ids)
      .then((found) => {
        if (live) setAssistedForms(found?.submissions || {});
      })
      .catch(() => {
        if (live) setAssistedForms({});
      });
    return () => {
      live = false;
    };
  }, [isOpen, submissionLinks, pschema]);

  const qualifyingFormLinks = submissionLinks.filter((link) => {
    const { form_id } = extractFormIds(link);
    if (!form_id) return false;
    const formName = formNames[form_id];
    return isQualifyingFormName(formName);
  });

  const hasQualifyingForms = qualifyingFormLinks.length > 0;
  const hasAnyForms = submissionLinks.length > 0;

  // Handle Process Forms button click - process all forms (template chosen by patient type: FFP/NP→NP, FFS/NS→NS, else NP)
  const handleProcessFormsClick = () => {
    if (readOnly) return;
    if (!onProcessForms) {
      console.log(
        "⚠️ FormsModal: Process Forms clicked but no onProcessForms callback provided",
      );
      return;
    }
    if (!hasAnyForms) {
      console.log(
        "⚠️ FormsModal: Process Forms button is disabled - no forms found",
      );
      return;
    }
    console.log("🔍 FormsModal: Processing forms:", submissionLinks.length);
    onProcessForms(submissionLinks);
  };

  // Handle Extract Text and Metadata for forms (force re-extraction)
  const handleExtractFormTextMetadata = async (link) => {
    if (readOnly) return;
    const { form_id, submission_id } = extractFormIds(link);

    if (!form_id || !submission_id) {
      toast({
        title: "Error",
        description:
          "Could not extract form ID and submission ID from the link",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const extractKey = `${form_id}_${submission_id}`;
    setExtractingForms((prev) => ({ ...prev, [extractKey]: true }));

    try {
      const { BASE_URLS } = await import("./env");
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(
        `${baseUrl}/api/ai-assistant/process-form-submission/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ form_id, submission_id, force: true, form_link: link, schema_id: pschema || supabaseSchema || "public" }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Failed to extract text and metadata",
        );
      }

      const data = await response.json();

      // Check if form hasn't been submitted yet
      if (data.error === "form_not_submitted") {
        toast({
          title: "Form Not Submitted",
          description:
            data.message ||
            "This form has been generated but not yet submitted by the patient.",
          status: "warning",
          duration: 7000,
          isClosable: true,
          containerStyle: { zIndex: 99999 },
        });
        return;
      }

      if (data.status === "error") {
        toast({
          title: "Could Not Extract Form Text",
          description: data.error || "No text could be extracted from this form.",
          status: "warning",
          duration: 7000,
          isClosable: true,
          containerStyle: { zIndex: 99999 },
        });
        return;
      }

      if (data.status !== "success") {
        throw new Error(data.error || "Failed to extract text and metadata");
      }

      toast({
        title: "Success",
        description: data.message || "Extracted text and metadata successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
        containerStyle: { zIndex: 99999 },
      });

      // Refresh forms metadata
      await fetchFormsData();
    } catch (error) {
      console.error("Error extracting form text/metadata:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to extract text and metadata",
        status: "error",
        duration: 5000,
        isClosable: true,
        containerStyle: { zIndex: 99999 },
      });
    } finally {
      setExtractingForms((prev) => ({ ...prev, [extractKey]: false }));
    }
  };

  // Fetch extracted data (full_text and summary) from database
  const handleViewExtractedData = async (link) => {
    const { form_id, submission_id } = extractFormIds(link);

    if (!form_id || !submission_id) {
      toast({
        title: "Error",
        description:
          "Could not extract form ID and submission ID from the link",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoadingExtractedData(true);
    setSelectedFormInfo({ form_id, submission_id });
    setSelectedFormLink(link); // Store the link for re-extraction
    setIsExtractedDataModalOpen(true);
    setExtractedData({ full_text: null, summary: null, updated_at: null });

    try {
      const supabase = await getSupabase();
      const client = pschema ? supabase.schema(pschema) : supabase;
      let { data, error } = await client
        .from("StoringFormSubmission")
        .select("full_text, summary, updated_at")
        .eq("form_id", form_id)
        .eq("submission_id", submission_id)
        .maybeSingle();

      if (error?.message?.includes("full_text") || error?.message?.includes("summary")) {
        ({ data, error } = await client
          .from("StoringFormSubmission")
          .select("updated_at")
          .eq("form_id", form_id)
          .eq("submission_id", submission_id)
          .maybeSingle());
      }

      if (error) {
        throw error;
      }

      if (data) {
        setExtractedData({
          full_text: data.full_text || null,
          summary: data.summary || null,
          updated_at: data.updated_at || null,
        });
      } else {
        setExtractedData({
          full_text: null,
          summary: null,
          updated_at: null,
        });
        toast({
          title: "No Data Found",
          description: "No extracted data found for this form submission",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error fetching extracted data:", error);
      toast({
        title: "Error",
        description: `Failed to fetch extracted data: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setExtractedData({ full_text: null, summary: null, updated_at: null });
    } finally {
      setIsLoadingExtractedData(false);
    }
  };

  // Handle re-extract from the extracted data modal
  const handleReExtractFormData = async () => {
    // For Epic forms, selectedFormLink is null — use selectedFormInfo directly
    let form_id, submission_id;
    if (selectedFormLink) {
      ({ form_id, submission_id } = extractFormIds(selectedFormLink));
    } else {
      ({ form_id, submission_id } = selectedFormInfo);
    }
    if (!form_id || !submission_id) return;

    setIsReExtractingFormData(true);
    try {
      const { BASE_URLS } = await import("./env");
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(
        `${baseUrl}/api/ai-assistant/process-form-submission/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ form_id, submission_id, force: true, form_link: selectedFormLink, schema_id: pschema || supabaseSchema || "public" }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Failed to re-extract text and metadata",
        );
      }

      const data = await response.json();

      if (data.error === "form_not_submitted") {
        toast({
          title: "Form Not Submitted",
          description:
            data.message ||
            "This form has been generated but not yet submitted by the patient.",
          status: "warning",
          duration: 7000,
          isClosable: true,
          containerStyle: { zIndex: 99999 },
        });
        return;
      }

      if (data.status !== "success") {
        throw new Error(data.error || "Failed to re-extract text and metadata");
      }

      toast({
        title: "Success",
        description:
          data.message || "Re-extracted text and metadata successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
        containerStyle: { zIndex: 99999 },
      });

      // Refresh the extracted data in the modal
      const supabase = await getSupabase();
      const client = pschema ? supabase.schema(pschema) : supabase;
      let { data: refreshedData, error: refreshError } = await client
        .from("StoringFormSubmission")
        .select("full_text, summary, updated_at")
        .eq("form_id", form_id)
        .eq("submission_id", submission_id)
        .maybeSingle();

      if (refreshError?.message?.includes("full_text") || refreshError?.message?.includes("summary")) {
        ({ data: refreshedData, error: refreshError } = await client
          .from("StoringFormSubmission")
          .select("updated_at")
          .eq("form_id", form_id)
          .eq("submission_id", submission_id)
          .maybeSingle());
      }

      if (!refreshError && refreshedData) {
        setExtractedData({
          full_text: refreshedData.full_text || null,
          summary: refreshedData.summary || null,
          updated_at: refreshedData.updated_at || null,
        });
      }

      // Refresh forms metadata
      await fetchFormsData();
    } catch (error) {
      console.error("Error re-extracting form text/metadata:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to re-extract text and metadata",
        status: "error",
        duration: 5000,
        isClosable: true,
        containerStyle: { zIndex: 99999 },
      });
    } finally {
      setIsReExtractingFormData(false);
    }
  };

  // Open the extracted data modal directly with data from an Epic form row
  const handleViewEpicFormText = (epicForm) => {
    setSelectedFormInfo({
      form_id: epicForm.form_id,
      submission_id: epicForm.submission_id,
    });
    setSelectedFormLink(null); // No URL for Epic forms
    setExtractedData({
      full_text: epicForm.full_text || null,
      summary: epicForm.summary || null,
      updated_at: null,
    });
    setIsExtractedDataModalOpen(true);
  };

  // Run Extract Text and Metadata for an Epic form using form_id + submission_id directly
  const handleExtractEpicForm = async (epicForm) => {
    const { form_id, submission_id } = epicForm;
    const extractKey = `${form_id}_${submission_id}`;
    setExtractingEpicForms((prev) => ({ ...prev, [extractKey]: true }));

    try {
      const { BASE_URLS } = await import("./env");
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(
        `${baseUrl}/api/ai-assistant/process-form-submission/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            form_id,
            submission_id,
            force: true,
            schema_id: pschema || supabaseSchema || "public",
          }),
        },
      );

      const data = await response.json();

      if (response.ok && data.status === "success") {
        toast({
          title: "Extracted",
          description: "Form text and metadata extracted successfully.",
          status: "success",
          duration: 4000,
          isClosable: true,
          containerStyle: { zIndex: 99999 },
        });
        // Refresh Epic forms so full_text shows up
        await fetchFormsData(false);
      } else {
        toast({
          title: "Extraction failed",
          description: data.error || "Unknown error",
          status: "error",
          duration: 5000,
          isClosable: true,
          containerStyle: { zIndex: 99999 },
        });
      }
    } catch (err) {
      console.error("Error extracting Epic form:", err);
      toast({
        title: "Extraction failed",
        description: err.message || "Network error",
        status: "error",
        duration: 5000,
        isClosable: true,
        containerStyle: { zIndex: 99999 },
      });
    } finally {
      setExtractingEpicForms((prev) => ({
        ...prev,
        [`${form_id}_${submission_id}`]: false,
      }));
    }
  };

  return (
    <>
      {/* Forms Modal - Non-blocking, draggable, resizable */}
      <DraggableResizableModal
        isOpen={isOpen}
        onClose={onClose}
        title={
          <>
            <FontAwesomeIcon icon={faFile} style={{ marginRight: "8px" }} />{" "}
            Patient Forms
          </>
        }
        defaultSize={{ width: 700, height: 500 }}
        minSize={{ width: 400, height: 300 }}
        zIndex={10000}
      >
        {isLoadingForms && isInitialLoad ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Spinner size="lg" />
            <p style={{ marginTop: "10px" }}>Loading forms...</p>
          </div>
        ) : submissionLinks.length === 0 && epicForms.length === 0 ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <p>No forms found for this patient.</p>
          </div>
        ) : (
          <>
            {/* Process Forms button - always visible when callback is provided, disabled when no qualifying forms */}
            {!readOnly && onProcessForms && (
              <div
                style={{
                  marginBottom: "16px",
                  padding: "12px",
                  backgroundColor: hasAnyForms ? "#f0f9ff" : "#f7fafc",
                  borderRadius: "8px",
                  border: hasAnyForms
                    ? "1px solid #0284c7"
                    : "1px solid #e2e8f0",
                  opacity: hasAnyForms ? 1 : 0.7,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <Text
                      fontWeight="600"
                      fontSize="sm"
                      color={hasAnyForms ? "blue.700" : "gray.500"}
                    >
                      {hasAnyForms
                        ? `${submissionLinks.length} form${submissionLinks.length > 1 ? "s" : ""} found`
                        : "No forms found"}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      {hasAnyForms
                        ? "Ready to process with AI"
                        : "No form submissions yet"}
                    </Text>
                  </div>
                  <Button
                    colorScheme="blue"
                    size="sm"
                    onClick={handleProcessFormsClick}
                    isDisabled={!hasAnyForms}
                    opacity={hasAnyForms ? 1 : 0.5}
                    cursor={hasAnyForms ? "pointer" : "not-allowed"}
                    title={
                      hasAnyForms
                        ? "Process forms with AI"
                        : "No forms to process"
                    }
                  >
                    Process Forms
                  </Button>
                </div>
              </div>
            )}
            {submissionLinks.length > 0 && (
              <ul style={{ listStyleType: "none", padding: 0 }}>
                {submissionLinks.map((link, index) => {
                  const { form_id, submission_id } = extractFormIds(link);
                  const metadataKey =
                    form_id && submission_id
                      ? `${form_id}_${submission_id}`
                      : null;
                  const metadata = metadataKey
                    ? formMetadata[metadataKey]
                    : null;
                  const formName = form_id ? formNames[form_id] : null;
                  const isQualifyingForm = isQualifyingFormName(formName);
                  // Check if this form is being processed in the background
                  const isBeingProcessed =
                    metadataKey && processingForms[metadataKey];
                  // Form is extracting if either user clicked extract OR background processing
                  const isExtracting =
                    extractingForms[metadataKey] || isBeingProcessed;

                  return (
                    <li
                      key={index}
                      style={{
                        marginBottom: "10px",
                        padding: "10px",
                        border: isQualifyingForm
                          ? "1px solid #0284c7"
                          : "1px solid #e2e8f0",
                        borderRadius: "8px",
                        backgroundColor: isBeingProcessed
                          ? "#fffbeb"
                          : isQualifyingForm
                            ? "#f0f9ff"
                            : "#fff",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom:
                            metadata &&
                            (metadata.PatientName || metadata.MetaData)
                              ? "8px"
                              : "0",
                        }}
                      >
                        <div
                          style={{
                            flex: 1,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "4px",
                          }}
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Legacy form docs live in the now-private files
                            // bucket; sign on open. WTF_forms / external URLs
                            // are returned unchanged by toSignedUrl.
                            const opened = await toSignedUrl(link);
                            console.log(
                              "🔍 Opening form submission URL:",
                              opened,
                            );
                            window.open(
                              opened,
                              "_blank",
                              "noopener,noreferrer",
                            );
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.currentTarget.click();
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <FontAwesomeIcon
                            icon={faFile}
                            style={{
                              marginRight: "8px",
                              color: isQualifyingForm ? "#0284c7" : "#d3d3d3",
                            }}
                          />
                          <span
                            style={{
                              color: "#000",
                              textDecoration: "underline",
                            }}
                          >
                            {formName || `Form Submission ${index + 1}`}
                          </span>
                          {/* Show submission date if available */}
                          {(metadata?.created_at || metadata?.updated_at) && (
                            <span
                              style={{
                                marginLeft: "8px",
                                fontSize: "11px",
                                color: "#718096",
                              }}
                              title={`Submitted: ${new Date(metadata.created_at || metadata.updated_at).toLocaleString()}`}
                            >
                              {new Date(
                                metadata.created_at || metadata.updated_at,
                              ).toLocaleDateString()}
                            </span>
                          )}
                          {isQualifyingForm && (
                            <span
                              style={{
                                marginLeft: "8px",
                                padding: "2px 6px",
                                backgroundColor: "#0284c7",
                                color: "#fff",
                                fontSize: "10px",
                                borderRadius: "4px",
                                fontWeight: "600",
                              }}
                            >
                              Qualifying
                            </span>
                          )}
                          {isBeingProcessed && (
                            <span
                              style={{
                                marginLeft: "8px",
                                display: "flex",
                                alignItems: "center",
                                color: "#d69e2e",
                                fontSize: "12px",
                              }}
                            >
                              <Spinner size="xs" mr={1} color="orange.500" />
                              Processing...
                            </span>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: "8px" }}>
                          {!readOnly && (
                            <Button
                              size="sm"
                              colorScheme={
                                !metadata && !isBeingProcessed ? "gray" : "green"
                              }
                              variant={
                                !metadata && !isBeingProcessed
                                  ? "outline"
                                  : "solid"
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!metadata && !isBeingProcessed) {
                                  // Metadata missing - check DB and prompt user
                                  handleMissingMetadataClick(link);
                                } else {
                                  handleExtractFormTextMetadata(link);
                                }
                              }}
                              isLoading={
                                isExtracting ||
                                (isCheckingDb &&
                                  dbMissingFormInfo.form_id === form_id &&
                                  dbMissingFormInfo.submission_id ===
                                    submission_id)
                              }
                              loadingText={
                                isCheckingDb
                                  ? "Checking DB..."
                                  : isBeingProcessed
                                    ? "Processing..."
                                    : "Extracting..."
                              }
                              spinnerPlacement="start"
                              isDisabled={isBeingProcessed}
                              title={
                                isBeingProcessed
                                  ? "Form is being processed automatically..."
                                  : !metadata
                                    ? "Click to check database for existing entry"
                                    : "Extract text and metadata from this form"
                              }
                              opacity={!metadata && !isBeingProcessed ? 0.7 : 1}
                            >
                              Extract Text and Metadata
                            </Button>
                          )}
                          {metadata?.full_text && (
                            <Button
                              size="sm"
                              colorScheme="blue"
                              variant="outline"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleViewExtractedData(link);
                              }}
                            >
                              View Extracted Data
                            </Button>
                          )}
                        </div>
                      </div>
                      {submission_id && assistedForms[submission_id] && (
                        <AssistedFormBadge info={assistedForms[submission_id]} />
                      )}
                      {/* Show processing message when background processing */}
                      {isBeingProcessed && !metadata?.full_text && (
                        <div
                          style={{
                            marginTop: "8px",
                            padding: "8px",
                            backgroundColor: "#fef3c7",
                            borderRadius: "4px",
                            fontSize: "12px",
                            color: "#92400e",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Spinner size="xs" mr={2} color="orange.600" />
                          Extracting text and metadata... This may take a
                          moment.
                        </div>
                      )}
                      {metadata &&
                        (metadata.PatientName || metadata.MetaData) && (
                          <div
                            style={{
                              marginTop: "8px",
                              padding: "8px",
                              backgroundColor: "#f7fafc",
                              borderRadius: "4px",
                              fontSize: "12px",
                              color: "#4a5568",
                            }}
                          >
                            {metadata.PatientName && (
                              <div style={{ marginBottom: "4px" }}>
                                <strong>Patient:</strong> {metadata.PatientName}
                              </div>
                            )}
                            {metadata.MetaData &&
                              typeof metadata.MetaData === "object" &&
                              (() => {
                                const isErrorString = (v) =>
                                  typeof v === "string" &&
                                  (v.includes("API error occurred") ||
                                    v.includes("Using generic questions") ||
                                    v.includes("AI service") ||
                                    v.includes("timed out"));
                                const renderValue = (v) => {
                                  if (Array.isArray(v)) {
                                    const labels = v.map((item) =>
                                      typeof item === "object" && item !== null
                                        ? item.question ||
                                          item.text ||
                                          item.label ||
                                          JSON.stringify(item)
                                        : String(item),
                                    );
                                    return labels.join("; ");
                                  }
                                  if (typeof v === "object" && v !== null)
                                    return JSON.stringify(v);
                                  return String(v);
                                };
                                const entries = Object.entries(
                                  metadata.MetaData,
                                ).filter(
                                  ([key, value]) =>
                                    key !== "questions" &&
                                    !isErrorString(value),
                                );
                                if (entries.length === 0) return null;
                                return (
                                  <div>
                                    <strong>Metadata:</strong>
                                    <ul
                                      style={{
                                        marginTop: "4px",
                                        marginLeft: "20px",
                                        listStyleType: "disc",
                                      }}
                                    >
                                      {entries.map(([key, value]) => (
                                        <li key={key}>
                                          <strong>{key}:</strong>{" "}
                                          {renderValue(value)}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                );
                              })()}
                          </div>
                        )}
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Epic Forms Section */}
            {epicForms.length > 0 && (
              <>
                {submissionLinks.length > 0 && (
                  <div
                    style={{
                      marginTop: "16px",
                      marginBottom: "10px",
                      borderTop: "1px solid #e2e8f0",
                      paddingTop: "12px",
                    }}
                  >
                    <Text fontWeight="600" fontSize="sm" color="purple.600">
                      Epic Forms
                    </Text>
                  </div>
                )}
                <ul style={{ listStyleType: "none", padding: 0 }}>
                  {epicForms.map((epicForm, index) => {
                    const extractKey = `${epicForm.form_id}_${epicForm.submission_id}`;
                    const isExtracting = extractingEpicForms[extractKey];
                    return (
                      <li
                        key={epicForm.id || index}
                        style={{
                          marginBottom: "10px",
                          padding: "10px",
                          border: "1px solid #d6bcfa",
                          borderRadius: "8px",
                          backgroundColor: "#faf5ff",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom:
                              epicForm.PatientName ||
                              epicForm.MetaData ||
                              epicForm.full_text
                                ? "8px"
                                : "0",
                          }}
                        >
                          <div
                            style={{
                              flex: 1,
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: "4px",
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faFile}
                              style={{ marginRight: "8px", color: "#805ad5" }}
                            />
                            <span style={{ color: "#000" }}>
                              {epicForm.PatientName
                                ? `${epicForm.PatientName} - Questionnaire`
                                : `Epic Questionnaire ${index + 1}`}
                            </span>
                            <span
                              style={{
                                marginLeft: "8px",
                                padding: "2px 6px",
                                backgroundColor: "#805ad5",
                                color: "#fff",
                                fontSize: "10px",
                                borderRadius: "4px",
                                fontWeight: "600",
                              }}
                            >
                              Epic
                            </span>
                            {/* Show submission date if available */}
                            {(epicForm.created_at || epicForm.updated_at) && (
                              <span
                                style={{
                                  marginLeft: "8px",
                                  fontSize: "11px",
                                  color: "#718096",
                                }}
                                title={`Submitted: ${new Date(epicForm.created_at || epicForm.updated_at).toLocaleString()}`}
                              >
                                {new Date(
                                  epicForm.created_at || epicForm.updated_at,
                                ).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          <div style={{ display: "flex", gap: "8px" }}>
                            {!readOnly && (
                              <Button
                                size="sm"
                                colorScheme="purple"
                                onClick={() => handleExtractEpicForm(epicForm)}
                                isLoading={isExtracting}
                                loadingText="Extracting..."
                              >
                                Extract Text and Metadata
                              </Button>
                            )}
                            {epicForm.full_text && (
                              <Button
                                size="sm"
                                colorScheme="blue"
                                variant="outline"
                                onClick={() => handleViewEpicFormText(epicForm)}
                              >
                                View Text
                              </Button>
                            )}
                          </div>
                        </div>
                        {(epicForm.PatientName ||
                          epicForm.MetaData ||
                          epicForm.full_text) && (
                          <div
                            style={{
                              marginTop: "8px",
                              padding: "8px",
                              backgroundColor: "#f7fafc",
                              borderRadius: "4px",
                              fontSize: "12px",
                              color: "#4a5568",
                            }}
                          >
                            {epicForm.PatientName && (
                              <div style={{ marginBottom: "4px" }}>
                                <strong>Patient:</strong> {epicForm.PatientName}
                              </div>
                            )}
                            {epicForm.MetaData &&
                              typeof epicForm.MetaData === "object" &&
                              (() => {
                                const isErrorString = (v) =>
                                  typeof v === "string" &&
                                  (v.includes("API error occurred") ||
                                    v.includes("Using generic questions") ||
                                    v.includes("AI service") ||
                                    v.includes("timed out"));
                                const renderValue = (v) => {
                                  if (Array.isArray(v)) {
                                    const labels = v.map((item) =>
                                      typeof item === "object" && item !== null
                                        ? item.question ||
                                          item.text ||
                                          item.label ||
                                          JSON.stringify(item)
                                        : String(item),
                                    );
                                    return labels.join("; ");
                                  }
                                  if (typeof v === "object" && v !== null)
                                    return JSON.stringify(v);
                                  return String(v);
                                };
                                const entries = Object.entries(
                                  epicForm.MetaData,
                                ).filter(
                                  ([key, value]) =>
                                    key !== "questions" &&
                                    !isErrorString(value),
                                );
                                if (entries.length === 0) return null;
                                return (
                                  <div>
                                    <strong>Metadata:</strong>
                                    <ul
                                      style={{
                                        marginTop: "4px",
                                        marginLeft: "20px",
                                        listStyleType: "disc",
                                      }}
                                    >
                                      {entries.map(([key, value]) => (
                                        <li key={key}>
                                          <strong>{key}:</strong>{" "}
                                          {renderValue(value)}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                );
                              })()}
                            {epicForm.full_text && (
                              <div
                                style={{
                                  marginTop:
                                    epicForm.PatientName || epicForm.MetaData
                                      ? "4px"
                                      : "0",
                                }}
                              >
                                <strong>Summary:</strong>{" "}
                                {epicForm.summary
                                  ? epicForm.summary.substring(0, 120) +
                                    (epicForm.summary.length > 120 ? "..." : "")
                                  : "Text extracted — click View Text to see."}
                              </div>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </>
        )}
      </DraggableResizableModal>

      {/* Extracted Data Modal - Non-blocking, draggable, resizable */}
      <DraggableResizableModal
        isOpen={isExtractedDataModalOpen}
        onClose={() => setIsExtractedDataModalOpen(false)}
        title={
          <>
            Extracted Form Data
            {selectedFormInfo.form_id && (
              <span
                style={{
                  fontSize: "12px",
                  color: "#718096",
                  marginLeft: "12px",
                  fontWeight: "normal",
                }}
              >
                Form: {selectedFormInfo.form_id.substring(0, 8)}...
              </span>
            )}
          </>
        }
        defaultSize={{ width: 800, height: 600 }}
        minSize={{ width: 400, height: 300 }}
        zIndex={10002}
        contentStyle={{ padding: "20px" }}
      >
        <div>
          {isLoadingExtractedData ? (
            <div style={{ textAlign: "center", padding: "40px 20px" }}>
              <Spinner size="lg" color="blue.500" />
              <p style={{ marginTop: "10px" }}>Loading extracted data...</p>
            </div>
          ) : (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "24px" }}
            >
              {/* Re-extract Button */}
              {!readOnly && (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={handleReExtractFormData}
                  isLoading={isReExtractingFormData}
                  loadingText="Re-extracting..."
                >
                  Re-extract
                </Button>
              </div>
              )}

              {/* Summary Section */}
              <div>
                <h3
                  style={{
                    fontWeight: "600",
                    marginBottom: "8px",
                    fontSize: "18px",
                  }}
                >
                  Summary
                </h3>
                {extractedData.summary ? (
                  <div
                    style={{
                      padding: "16px",
                      backgroundColor: "#EBF8FF",
                      borderRadius: "6px",
                      border: "1px solid #90CDF4",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "14px",
                        whiteSpace: "pre-wrap",
                        margin: 0,
                      }}
                    >
                      {extractedData.summary}
                    </p>
                  </div>
                ) : (
                  <div
                    style={{
                      padding: "16px",
                      backgroundColor: "#F7FAFC",
                      borderRadius: "6px",
                      textAlign: "center",
                    }}
                  >
                    <p
                      style={{ color: "#A0AEC0", fontSize: "14px", margin: 0 }}
                    >
                      No summary available. Click "Extract Text and Metadata" to
                      generate.
                    </p>
                  </div>
                )}
              </div>

              {/* Full Text Section */}
              <div>
                {extractedData.updated_at && (
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#718096",
                      marginBottom: "4px",
                    }}
                  >
                    Last extracted:{" "}
                    {new Date(extractedData.updated_at).toLocaleString()}
                  </p>
                )}
                <h3
                  style={{
                    fontWeight: "600",
                    marginBottom: "8px",
                    fontSize: "18px",
                  }}
                >
                  Full Text{" "}
                  {extractedData.full_text
                    ? `(${extractedData.full_text.length} characters)`
                    : ""}
                </h3>
                {extractedData.full_text ? (
                  <div
                    style={{
                      padding: "16px",
                      backgroundColor: "#F7FAFC",
                      borderRadius: "6px",
                      border: "1px solid #E2E8F0",
                    }}
                  >
                    <pre
                      style={{
                        fontSize: "14px",
                        whiteSpace: "pre-wrap",
                        fontFamily: "monospace",
                        color: "#4A5568",
                        margin: 0,
                      }}
                    >
                      {extractedData.full_text}
                    </pre>
                  </div>
                ) : (
                  <div
                    style={{
                      padding: "16px",
                      backgroundColor: "#F7FAFC",
                      borderRadius: "6px",
                      textAlign: "center",
                    }}
                  >
                    <p
                      style={{ color: "#A0AEC0", fontSize: "14px", margin: 0 }}
                    >
                      No full text available. Click "Extract Text and Metadata"
                      to extract.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </DraggableResizableModal>

      {/* DB Entry Missing Confirmation Modal */}
      <Modal
        isOpen={isDbMissingModalOpen}
        onClose={closeDbMissingModal}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {existingDbEntry
              ? "Update Database Entry?"
              : "Create Database Entry?"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {existingDbEntry ? (
              <Box>
                <Text mb={3}>
                  Found an existing database entry for this form with a
                  different submission ID.
                </Text>
                <Box bg="gray.100" p={3} borderRadius="md" fontSize="sm" mb={3}>
                  <Text>
                    <strong>Existing submission_id:</strong>{" "}
                    {existingDbEntry.submission_id?.substring(0, 8)}...
                  </Text>
                  <Text>
                    <strong>New submission_id:</strong>{" "}
                    {dbMissingFormInfo.submission_id?.substring(0, 8)}...
                  </Text>
                </Box>
                <Text>
                  Would you like to update the existing entry to use the new
                  submission ID?
                </Text>
              </Box>
            ) : (
              <Box>
                <Text mb={3}>
                  No database entry found for this form submission. The form URL
                  exists but there is no corresponding entry in the
                  StoringFormSubmission table.
                </Text>
                <Box bg="gray.100" p={3} borderRadius="md" fontSize="sm" mb={3}>
                  <Text>
                    <strong>Form ID:</strong>{" "}
                    {dbMissingFormInfo.form_id?.substring(0, 8)}...
                  </Text>
                  <Text>
                    <strong>Submission ID:</strong>{" "}
                    {dbMissingFormInfo.submission_id?.substring(0, 8)}...
                  </Text>
                </Box>
                <Text>
                  Would you like to create a new database entry for this form?
                </Text>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={closeDbMissingModal}
              isDisabled={isCreatingDbEntry}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleConfirmDbEntry}
              isLoading={isCreatingDbEntry}
              loadingText={existingDbEntry ? "Updating..." : "Creating..."}
            >
              {existingDbEntry ? "Update Entry" : "Create Entry"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default FormsModal;
